import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Icon } from './Icon';
import { EmptyState } from './primitives';

export interface Column<T> {
    id: string;
    label: string;
    render: (row: T) => ReactNode;
    sortValue?: (row: T) => string | number;
    align?: 'right' | 'left';
    optional?: boolean;
}
export function DataTable<T>({ columns, rows, rowKey, name, emptyTitle = 'No results found', emptyDescription = 'Try another search or change your filters.', pageSize = 12 }: {
    columns: Column<T>[];
    rows: T[];
    rowKey: (row: T) => string;
    name: string;
    emptyTitle?: string;
    emptyDescription?: string;
    pageSize?: number;
}) {
    const [sort, setSort] = useState<{ column: string; direction: 1 | -1 } | null>(null);
    const [page, setPage] = useState(0);
    const [optional, setOptional] = useState<string[]>(() => {
        try { const value = JSON.parse(localStorage.getItem(`dexa:columns:${name}`) || '[]'); return Array.isArray(value) ? value.filter(item => typeof item === 'string') : []; } catch { return []; }
    });
    const ordered = useMemo(() => {
        const column = columns.find(column => column.id === sort?.column);
        if (!column?.sortValue || !sort) return rows;
        const sortValue = column.sortValue;
        return [...rows].sort((left, right) => {
            const a = sortValue(left), b = sortValue(right);
            return (typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b))) * sort.direction;
        });
    }, [rows, sort, columns]);
    const pages = Math.max(1, Math.ceil(rows.length / pageSize));
    const current = Math.min(page, pages - 1);
    const visible = columns.filter(column => !column.optional || optional.includes(column.id));
    function toggle(id: string) {
        const next = optional.includes(id) ? optional.filter(value => value !== id) : [...optional, id];
        setOptional(next);
        try { localStorage.setItem(`dexa:columns:${name}`, JSON.stringify(next)); } catch { /* Storage is optional. */ }
    }
    return <div className="data-table">
        {rows.length === 0 ? <EmptyState title={emptyTitle} description={emptyDescription} /> : <div className="table-scroll"><table aria-label={name}><thead><tr>{visible.map(column => <th scope="col" key={column.id} className={column.align === 'right' ? 'align-right' : ''} aria-sort={sort?.column === column.id ? sort.direction === 1 ? 'ascending' : 'descending' : undefined}>
            {column.sortValue ? <button className="sort-button" onClick={() => { setSort({ column: column.id, direction: sort?.column === column.id && sort.direction === 1 ? -1 : 1 }); setPage(0); }}>{column.label}<span aria-hidden="true">{sort?.column === column.id ? sort.direction === 1 ? '↑' : '↓' : '↕'}</span></button> : column.label}
        </th>)}</tr></thead><tbody>{ordered.slice(current * pageSize, (current + 1) * pageSize).map(row => <tr key={rowKey(row)}>{visible.map(column => <td key={column.id} className={column.align === 'right' ? 'align-right' : ''}>{column.render(row)}</td>)}</tr>)}</tbody></table></div>}
        <div className="table-footer"><span>{rows.length === 0 ? '0 results' : `${current * pageSize + 1}–${Math.min((current + 1) * pageSize, rows.length)} of ${rows.length}`}</span>
            <div className="table-footer-actions">{columns.some(column => column.optional) && <details className="column-picker"><summary><Icon name="filter" size={15} />Columns</summary><div>{columns.filter(column => column.optional).map(column => <label key={column.id}><input type="checkbox" checked={optional.includes(column.id)} onChange={() => toggle(column.id)} />{column.label}</label>)}</div></details>}
                <button className="icon-button" aria-label="Previous page" disabled={current === 0} onClick={() => setPage(current - 1)}><Icon name="left" size={17} /></button><span>{current + 1} / {pages}</span><button className="icon-button" aria-label="Next page" disabled={current >= pages - 1} onClick={() => setPage(current + 1)}><Icon name="right" size={17} /></button>
            </div>
        </div>
    </div>;
}
